import type { AgentEventDisplay } from '@tabbio/types';
import type { AgentToolStage, JSONObject, JSONValue } from '../../../hooks/agent-stream';

export interface ToolCardProps {
  toolCallId: string;
  toolName: string;
  cardType?: string;
  args: JSONObject;
  result?: JSONValue;
  isError?: boolean;
  stage: AgentToolStage;
  display?: AgentEventDisplay;
  category?: string;
  entityType?: string;
  entityId?: string;
  approvalId?: string;
}

/** Safely read a nested value from unknown data. */
export function safeGet<T = unknown>(obj: unknown, ...keys: string[]): T | undefined {
  let current: unknown = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object' || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current as T | undefined;
}

/** Safely read an array from result. */
export function safeArray<T = unknown>(obj: unknown, key: string): T[] {
  const val = safeGet<unknown[]>(obj, key);
  return Array.isArray(val) ? (val as T[]) : [];
}

/** If value is itself an array, return it. */
export function asArray<T = unknown>(val: unknown): T[] {
  return Array.isArray(val) ? (val as T[]) : [];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format an ISO date string or Date to "Mon YYYY" (e.g., "Feb 2023"). Returns empty string for invalid input. */
export function formatDate(value: unknown): string {
  if (!value) return '';
  try {
    const d = new Date(value as string);
    if (isNaN(d.getTime())) return '';
    return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return typeof value === 'string' ? value.slice(0, 7) : '';
  }
}

/** Format a date range: "Feb 2023 – Present" or "Feb 2023 – Nov 2024" */
export function formatDateRange(start: unknown, end: unknown, current?: boolean): string {
  const s = formatDate(start);
  if (!s) return '';
  const e = current ? 'Present' : formatDate(end);
  return e ? `${s} – ${e}` : s;
}
